import {graphql, useStaticQuery, Link as GatsbyLink} from 'gatsby';
import React, {Fragment, memo, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import CSSTransition from 'react-transition-group/CSSTransition';

import ArrowLeft from '../images/arrow_left.svg';
import ArrowRight from '../images/arrow_right.svg';
import {useDispatch, useSelector} from '../store.js';
import style from './nav.module.scss';
import {styleMods, unreachable} from '../utils';

import IntlString from './intl-string';
import LocaleLink from './locale-link';
import { usePageProps } from '../hooks';

/** @typedef {{type: "simple", title: string, path: string}
 *   | {type: "artists"} | {type: "artists-back"}
 *   | {type: "artist", target: string, text: import('../../tmp/graphql-types').IntlStringFragment}
 * } NavItem */

/** @typedef {(_: "root" | "artists") => void} SetCurPanel */

/**
 * @param {import("../page-metadata").PageMetadata} pageMeta
 * @param {import("react-intl").IntlShape} intl
 * @param {import("gatsby").PageProps["location"]} location
 */
function renderPanelLangButton(pageMeta, intl, location) {
	const pagePath = pageMeta[intl.locale === 'de' ? 'pathEn' : 'pathDe'];
	const to = `${pagePath}${location.search}${location.hash}`;
	return (
		<GatsbyLink
			to={to}
			className={style.itemLangSwitcherLink}
			hrefLang={intl.locale === 'de' ? 'en' : 'de'}
			title={intl.locale === 'de' ? 'English' : 'Deutsch'}
		>
			{intl.locale === 'de' ? 'en' : 'de'}
		</GatsbyLink>
	);
}

/**
 * @param {NavItem} item
 * @param {import('react-intl').IntlShape} intl
 * @param {SetCurPanel} setCurPanel
 */
function renderPanelItem(item, intl, setCurPanel) {
	switch (item.type) {
		case 'simple':
			return (
				<LocaleLink
					to={item.path}
					className={style.itemLink}
				>
					<FormattedMessage id={item.title} />
				</LocaleLink>
			);

		case 'artists-back':
			return (
				<button
					className={style.itemLink}
					onClick={() => {
						setCurPanel('root');
					}}
					title={intl.formatMessage({id: 'nav.back'})}
				>
					<ArrowLeft className={style.itemArrowLeft} />
					<FormattedMessage id="nav.back" />
				</button>
			);

		case 'artists':
			return (
				<button
					className={style.itemLink}
					onClick={() => {
						setCurPanel('artists');
					}}
				>
					<span>
						<FormattedMessage id="nav.artists" />
					</span>
					<ArrowRight className={style.itemArrowRight} />
				</button>
			);

		case 'artist':
			return (
				<LocaleLink
					to={`/map#${item.target}`}
					className={style.itemLink}
				>
					<IntlString {...item.text} />
				</LocaleLink>
			);

		default:
			unreachable();
	}
}

const Panel = memo(
	/**
	 * @param {{
	 *   items: Array<NavItem>,
	 *   isVisible: boolean,
	 *   setCurPanel: SetCurPanel,
	 *   panelName: string,
	 * }} param0
	 */
	function Panel({items, isVisible, setCurPanel, panelName}) {
		const intl = useIntl();
		const pageProps = usePageProps();

		return (
			<ul
				className={styleMods(style, 'list', [isVisible && 'visible', panelName])}
			>
				{items.map((item, i) => (
					<li
						key={i}
						className={styleMods(style, 'item', 'type' in item ? item.type : 'default')}
					>
						{/* {i === 0 && pageProps.pageContext.meta && renderPanelLangButton(
							pageProps.pageContext.meta,
							intl,
							pageProps.location,
						)} */}
						{renderPanelItem(item,intl, setCurPanel)}
					</li>
				))}
			</ul>
		);
	}
);

export default memo(
	function Nav() {
		const open = useSelector((s) => s.navOpen);
		const dispatch = useDispatch();

		/** @type {import("../../tmp/graphql-types").NavQuery} */
		const data = useStaticQuery(graphql`
			query Nav {
				allArtistsJson(filter: {visibleInNav: {eq: true}}, sort: {order: ASC, fields: order}) {
					nodes {
						slug
						name {
							...IntlString
						}
					}
				}
			}
		`);

		/** @type {[
		 *   "artists" | "root",
		 *   React.Dispatch<React.SetStateAction<"artists" | "root">>,
		 * ]} */
		const curPanel_ = useState('root');
		const [curPanel, setCurPanel] = curPanel_;

		useEffect(
			() => {
				// reset source
				setCurPanel('root');

				if (!open) {
					return;
				}

				// add escape key listener to close nav
				/**
				 * @param {KeyboardEvent} e
				 */
				function listener(e) {
					if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
						return;
					}

					if (e.key === 'Escape') {
						dispatch({type: 'closeNav'});
						e.preventDefault();
						e.stopPropagation();
					}
				}

				window.addEventListener('keydown', listener);
				return () => {
					window.removeEventListener('keydown', listener);
				};
			},
			[open, setCurPanel, dispatch],
		);

		const panels = [
			{
				panelName: 'root',
				/** @type {Array<NavItem>} */
				items: [
					{type: 'simple', title: 'nav.artworks', path: '/map'},
					{type: 'artists'},
					{type: 'simple', title: 'nav.blog', path: '/blog'},
					{type: 'simple', title: 'nav.events', path: '/events'},
					// {type: 'simple', title: 'nav.lightsOff', path: '/lights-off'},
					{type: 'simple', title: 'nav.sponsors', path: '/sponsors'},
					{type: 'simple', title: 'nav.press', path: '/press'},
					{type: 'simple', title: 'nav.contact', path: '/contact'},
				],
			},
			{
				panelName: 'artists',
				items: [
					{type: 'artists-back'},
					...data.allArtistsJson.nodes.map((art) => {
						/** @type {NavItem} */
						const d = {
							type: 'artist',
							text: art.name,
							target: art.slug,
						};
						return d;
					}),
				],
			}
		]
			.map(({panelName, items}) => (
				<Panel
					key={panelName}
					panelName={panelName}
					items={items}
					isVisible={panelName === curPanel}
					setCurPanel={setCurPanel}
				/>
			));

		return (
			<Fragment>
				<CSSTransition
					in={open}
					timeout={400}
					classNames={{
						enter: style.overlayEnter,
						enterActive: style.overlayEnterActive,
						enterDone: style.overlayEnterDone,
						exit: style.overlayExit,
						exitActive: style.overlayExitActive,
					}}
					mountOnEnter={true}
					unmountOnExit={true}
				>
					<div
						className={style.overlay}
						tabIndex={-1}
						onClick={() => {
							dispatch({type: 'closeNav'});
						}}
					/>
				</CSSTransition>

				<CSSTransition
					in={open}
					timeout={400}
					classNames={{
						enter: style.baseEnter,
						enterActive: style.baseEnterActive,
						enterDone: style.baseEnterDone,
						exit: style.baseExit,
						exitActive: style.baseExitActive,
					}}
				>
					<nav className={style.base}>
						{panels}
					</nav>
				</CSSTransition>
			</Fragment>
		);
	}
);
